import {
  AddCircleOutlined,
  Delete,
  Edit,
  GroupsOutlined,
  PeopleOutlined,
} from "@mui/icons-material";
import { Card, Chip, Typography } from "@mui/material";
import { useState } from "react";

import { ConfirmationModal } from "@components";
import { theme } from "@config";
import { useGetUsersByOrg } from "@hooks";
import {
  useCreateFunctionalGroupMutation,
  useDeleteFunctionalGroupMutation,
  useUpdateFunctionalGroupMutation,
  useAddFunctionalGroupMemberMutation,
  useRemoveFunctionalGroupMemberMutation,
} from "src/hooks/mutations";
import { useGetFunctionalGroups } from "src/hooks/queries";
import { FunctionalGroup } from "src/types";
import { getOrgMembership, getUserDisplayName } from "src/utils/user";
import GenericButton from "../GenericButton/GenericButton";
import { CreateFunctionalGroupModal } from "./CreateFunctionalGroupModal";
import { ManageMembersModal } from "./ManageMembersModal";

type Props = {
  orgId: string;
};

export const FunctionalGroupsSection = ({ orgId }: Props) => {
  const { data: groups } = useGetFunctionalGroups(orgId);
  const { data: orgUsers } = useGetUsersByOrg({ orgId });

  const createMutation = useCreateFunctionalGroupMutation();
  const updateMutation = useUpdateFunctionalGroupMutation();
  const deleteMutation = useDeleteFunctionalGroupMutation();
  const addMemberMutation = useAddFunctionalGroupMemberMutation();
  const removeMemberMutation = useRemoveFunctionalGroupMemberMutation();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<FunctionalGroup | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<FunctionalGroup | null>(null);
  const [manageMembersGroup, setManageMembersGroup] =
    useState<FunctionalGroup | null>(null);

  const handleCreate = async (data: {
    name: string;
    acronym: string;
    administerAllTrainings: boolean;
  }) => {
    await createMutation.mutateAsync({ orgId, data });
    setCreateModalOpen(false);
  };

  const handleUpdate = async (data: {
    name: string;
    acronym: string;
    administerAllTrainings: boolean;
  }) => {
    if (!editingGroup) return;
    await updateMutation.mutateAsync({
      orgId,
      groupId: editingGroup.id,
      data,
    });
    setEditingGroup(null);
  };

  const handleDelete = async () => {
    if (!deleteGroup) return;
    await deleteMutation.mutateAsync({ orgId, groupId: deleteGroup.id });
    setDeleteGroup(null);
  };

  const handleAddMember = async (organizationMembershipId: string) => {
    if (!manageMembersGroup) return;
    await addMemberMutation.mutateAsync({
      orgId,
      groupId: manageMembersGroup.id,
      organizationMembershipId,
    });
  };

  const handleRemoveMember = async (membershipId: string) => {
    if (!manageMembersGroup) return;
    await removeMemberMutation.mutateAsync({
      orgId,
      groupId: manageMembersGroup.id,
      membershipId,
    });
  };

  // Derive member info from orgUsers data
  const getMembersForGroup = (groupId: string) => {
    if (!orgUsers) return [];
    const members: {
      organizationMembershipId: string;
      functionalGroupMembershipId: string;
      userName: string;
      email: string;
    }[] = [];

    for (const user of orgUsers) {
      const orgMembership = getOrgMembership(user, orgId);
      if (!orgMembership) continue;

      const fgMemberships = orgMembership.functionalGroupMemberships || [];
      for (const fgm of fgMemberships) {
        if (fgm.functionalGroupId === groupId) {
          members.push({
            organizationMembershipId: orgMembership.id,
            functionalGroupMembershipId: fgm.id,
            userName: getUserDisplayName(user),
            email: user.email,
          });
        }
      }
    }
    return members;
  };

  const getAvailableUsersForGroup = (groupId: string) => {
    if (!orgUsers) return [];
    const currentMemberOrgMembershipIds = new Set(
      getMembersForGroup(groupId).map((m) => m.organizationMembershipId)
    );

    return orgUsers
      .filter((user) => {
        const orgMembership = getOrgMembership(user, orgId);
        return (
          orgMembership &&
          orgMembership.activated !== false &&
          !currentMemberOrgMembershipIds.has(orgMembership.id)
        );
      })
      .map((user) => {
        const orgMembership = getOrgMembership(user, orgId);
        return {
          organizationMembershipId: orgMembership!.id,
          userName: getUserDisplayName(user),
          email: user.email,
        };
      });
  };

  return (
    <>
      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <GroupsOutlined />
          <Typography
            variant="body1"
            data-testid="functional-groups-subheader"
          >
            Functional Groups
          </Typography>
          <GenericButton
            id="create-functional-group-button"
            variant="contained"
            onClick={() => setCreateModalOpen(true)}
            tooltipContent="Create Functional Group"
            color="primary"
          >
            <AddCircleOutlined fontSize="small" color="primary" />
          </GenericButton>
        </div>

        {(!groups || groups.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            No functional groups created yet.
          </Typography>
        )}

        <div className="flex flex-col gap-2">
          {groups?.map((group) => (
            <Card
              key={group.id}
              className="flex items-center gap-2"
              sx={{
                border: "1px solid rgba(224, 224, 224, 1)",
                fontSize: "1rem",
                boxShadow: "none",
                lineHeight: 2,
                padding: "9px 10px",
                backgroundColor: "transparent",
              }}
            >
              <Chip
                label={group.acronym}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Typography variant="body1" sx={{ flex: 1 }}>
                {group.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {group._count?.members ?? 0} member
                {(group._count?.members ?? 0) !== 1 ? "s" : ""}
              </Typography>
              <GenericButton
                size="small"
                aria-label="Manage members"
                onClick={() => setManageMembersGroup(group)}
                tooltipContent="Manage Members"
              >
                <PeopleOutlined
                  fontSize="small"
                  style={{ color: theme.palette.grey[500] }}
                />
              </GenericButton>
              <GenericButton
                size="small"
                aria-label="Edit functional group"
                onClick={() => setEditingGroup(group)}
                tooltipContent="Edit Functional Group"

              >
                <Edit
                  fontSize="small"
                  style={{ color: theme.palette.grey[500] }}
                />
              </GenericButton>
              <GenericButton
                size="small"
                aria-label="Delete functional group"
                onClick={() => setDeleteGroup(group)}
                tooltipContent="Delete Functional Group"
              >
                <Delete fontSize="small" color="error" />
              </GenericButton>
            </Card>
          ))}
        </div>
      </div>

      <CreateFunctionalGroupModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreate}
        isLoading={createMutation.isPending}
      />

      <CreateFunctionalGroupModal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        onSave={handleUpdate}
        existingGroup={editingGroup}
        isLoading={updateMutation.isPending}
      />

      <ManageMembersModal
        isOpen={!!manageMembersGroup}
        onClose={() => setManageMembersGroup(null)}
        group={manageMembersGroup}
        members={manageMembersGroup ? getMembersForGroup(manageMembersGroup.id) : []}
        availableUsers={
          manageMembersGroup
            ? getAvailableUsersForGroup(manageMembersGroup.id)
            : []
        }
        isSaving={addMemberMutation.isPending || removeMemberMutation.isPending}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
      />

      <ConfirmationModal
        open={!!deleteGroup}
        onAbort={() => setDeleteGroup(null)}
        onConfirm={handleDelete}
        title="Delete Functional Group"
        content={`Are you sure you want to delete "${deleteGroup?.name}"? All member associations will be removed.`}
      />
    </>
  );
};
